/**
 * @file The labelled Extract schedule button for one attachment version.
 * Consumers: `AttachmentListActions` (each row, after Download) and
 * `AttachmentPreviewOverlay` (its header, after Download).
 *
 * It wears ApprovalBar's tide wash while enabled, so it reads as the start of
 * the schedule review rather than as a plain row icon. A blocked button is
 * disabled, never hidden, and its tooltip says why; the wrapping span lets the
 * tooltip fire over a disabled button.
 */
import { FC } from 'react';
import { Button, Tooltip, theme } from 'antd';
import { TableOutlined } from '@ant-design/icons';
import type {} from '@/providers/antdAliasToken';

export const ExtractScheduleButton: FC<{
  id: string;
  fileName: string;
  blockedReason: string | null;
  busy: boolean;
  loading: boolean;
  onClick: () => void;
}> = ({ id, fileName, blockedReason, busy, loading, onClick }) => {
  const { token } = theme.useToken();
  const label = `Extract schedule from ${fileName}`;
  const disabled = busy || blockedReason !== null;
  return (
    <Tooltip title={blockedReason ?? label}>
      <span style={{ display: 'inline-flex', marginInline: 5 }}>
        <Button
          id={id}
          size="small"
          icon={<TableOutlined />}
          aria-label={label}
          disabled={disabled}
          loading={loading}
          onClick={onClick}
          style={
            disabled
              ? undefined
              : {
                  background: token.colorTideWash,
                  color: token.colorTideDeep,
                  borderColor: token.colorTide,
                }
          }
        >
          Extract schedule
        </Button>
      </span>
    </Tooltip>
  );
};
